// Employee H5 API client
import type { ApiResponse } from '@kc/shared';

const API_BASE = '/api/v1';

// 微信换绑自愈：微信内 UA 判定
export function inWeChat(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /micromessenger/i.test(navigator.userAgent.toLowerCase());
}

// 微信换绑自愈：清掉本地登录态（含历史残留的租户头）
export function clearLocalAuth(): void {
  localStorage.removeItem('emp_token');
  localStorage.removeItem('emp_code');
  localStorage.removeItem('emp_tenant_id');
  localStorage.removeItem('emp_token_exp');
}

// 微信换绑自愈：40303 后静默走一次网页授权，换取“当前绑定工号”的新 token。
// 用 sessionStorage 做一次性标记：仅首次 40303 触发重授权；重授权后请求一旦成功即清标记，
// 若仍 403（真·他企账号）则直接抛错显示“无权限”，避免死循环/白屏。
const REAUTH_FLAG = 'kc_wx_reauth';

function recoverViaWxReauth(): void {
  const cur = window.location.pathname + window.location.search;
  sessionStorage.setItem(REAUTH_FLAG, '1');
  clearLocalAuth();
  // 微信授权后回到 /auth，由其完成 code 换 token 并跳回 cur
  window.location.href = `/auth?redirect=${encodeURIComponent(cur)}&wx=1`;
}

function getHeaders(): Record<string, string> {
  const token = localStorage.getItem('emp_token') || '';
  const tenantId = localStorage.getItem('emp_tenant_id') || '';
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`,
    'X-Tenant-ID': tenantId,
  };
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const resp = await fetch(`${API_BASE}${path}`, {
    method,
    headers: getHeaders(),
    body: body ? JSON.stringify(body) : undefined,
  });
  // 401 未登录 / 403 无权限或已停用：交给上层 authedRequest 处理
  if (resp.status === 401 || resp.status === 403) {
    let code = 0;
    let message = 'UNAUTHORIZED';
    try {
      const j: ApiResponse<unknown> = await resp.json();
      code = j.code;
      message = j.message || message;
    } catch { /* ignore */ }
    // 40302：账号停用；40303：企业定制题未授权（不跳登录，原地提示）；
    // 40305：凭证已被作废（微信换绑/解绑后旧 token），微信内静默重授权、微信外回登录页
    let tag = 'UNAUTHORIZED';
    if (resp.status === 403 && code === 40302) tag = 'ACCOUNT_DISABLED';
    else if (resp.status === 403 && code === 40303) tag = 'DECK_FORBIDDEN';
    else if (resp.status === 403 && code === 40305) tag = 'CRED_REVOKED';
    const err = new Error(message || tag);
    (err as Error & { status?: number; bizCode?: number; bizMessage?: string; tag?: string }).status = resp.status;
    (err as Error & { status?: number; bizCode?: number; bizMessage?: string; tag?: string }).bizCode = code;
    (err as Error & { status?: number; bizCode?: number; bizMessage?: string; tag?: string }).bizMessage = message;
    (err as Error & { status?: number; bizCode?: number; bizMessage?: string; tag?: string }).tag = tag;
    throw err;
  }
  const data: ApiResponse<T> = await resp.json();
  if (data.code !== 0) throw new Error(data.message);
  return data.data;
}

// 静默请求：401 不跳转登录页（用于公开可访问页面的可选埋点）
async function silentRequest<T>(method: string, path: string, body?: unknown): Promise<T | null> {
  try {
    return await request<T>(method, path, body);
  } catch {
    return null;
  }
}

// 拦截 401：自动清 token 跳登录页；40302：账号停用，清 token 并提示
// 40303：企业定制题未授权；微信内首次遇到时静默重新网页授权一次（自愈换绑后的旧 token），
//        已重试过仍 403 则向上抛错由答题页提示“无权限”。
export async function authedRequest<T>(method: string, path: string, body?: unknown): Promise<T> {
  try {
    const data = await request<T>(method, path, body);
    // 请求成功：说明当前 token 有效，解除一次性重授权标记
    sessionStorage.removeItem(REAUTH_FLAG);
    return data;
  } catch (e) {
    const tag = (e as Error & { tag?: string })?.tag || '';
    if (tag === 'DECK_FORBIDDEN') {
      // 微信内：可能是学员换绑新企业后本地仍用旧 token（旧 tenant_id）。
      // 仅在“本会话尚未重试过”时静默重新授权一次；已重试过仍无权限 → 原样上抛，避免死循环/白屏。
      if (inWeChat() && sessionStorage.getItem(REAUTH_FLAG) !== '1') {
        recoverViaWxReauth();
        // 跳转中：挂起，不再继续抛错渲染“无权限”（等待整页跳走）
        await new Promise<void>(() => { /* never resolves */ });
      }
      throw e;
    }
    if (tag === 'CRED_REVOKED') {
      // 旧 token 已因换绑/解绑被后端作废：微信内静默重新网页授权一次换取当前绑定工号的新 token，
      // 非微信环境（或已重授权仍被作废）则清登录态回登录页。一次性标记防死循环。
      if (inWeChat() && sessionStorage.getItem(REAUTH_FLAG) !== '1') {
        recoverViaWxReauth();
        await new Promise<void>(() => { /* never resolves */ });
      }
      clearLocalAuth();
      const cur = window.location.pathname + window.location.search;
      if (!cur.startsWith('/auth')) {
        window.location.href = `/auth?redirect=${encodeURIComponent(cur)}&tip=${encodeURIComponent('登录状态已失效，请重新登录')}`;
      }
      throw e;
    }
    if (tag === 'UNAUTHORIZED' || tag === 'ACCOUNT_DISABLED') {
      localStorage.removeItem('emp_token');
      localStorage.removeItem('emp_code');
      localStorage.removeItem('emp_tenant_id');
      const cur = window.location.pathname + window.location.search;
      if (!cur.startsWith('/auth')) {
        const tip = tag === 'ACCOUNT_DISABLED' ? '账号已停用，请联系管理员' : '请先登录';
        window.location.href = `/auth?redirect=${encodeURIComponent(cur)}&tip=${encodeURIComponent(tip)}`;
      }
    }
    throw e;
  }
}

export const authApi = {
  verify: (code: string, tenantId: string) =>
    request<{ access_token: string; refresh_token: string; expires_at: number; employee: { code: string } }>(
      'POST', '/auth/verify', { code, tenant_id: tenantId }
    ),
};

// 微信服务号对接（一键登录 / 绑定）
export interface WxOauthResult {
  bound: boolean;
  needs_bind?: boolean;
  openid?: string;
  subscribed?: number;
  access_token?: string;
  expires_at?: number;
  mp_name?: string;
  employee?: { code: string; public_id: string; tenant_id: string };
}

export interface WxRegisterStatus {
  openid: string;
  subscribed: number;
  bound: number;
  ready: number;
  mp_name?: string;
  access_token?: string;
  expires_at?: number;
}

export const wxApi = {
  oauthUrl: (redirect = '/auth') =>
    request<{ url: string; redirect_uri: string }>('GET', `/wx/oauth/url?redirect=${encodeURIComponent(redirect)}`),
  oauthCallback: (code: string) =>
    request<WxOauthResult>('GET', `/wx/oauth/callback?code=${encodeURIComponent(code)}`),
  completeOauth: (code: string) =>
    request<WxOauthResult>('GET', `/wx/oauth/callback?code=${encodeURIComponent(code)}`),
  bind: (employeeCode: string, tenantId: string, openid: string) =>
    request<WxOauthResult & { bound: boolean }>('POST', '/wx/bind', {
      employee_code: employeeCode, tenant_id: tenantId, openid,
    }),
  bindStatus: (openid: string) =>
    request<{ bound: boolean; employee_code?: string; status?: string }>('GET', `/wx/bind-status?openid=${encodeURIComponent(openid)}`),
  registerStatus: (openid: string) =>
    request<WxRegisterStatus>('GET', `/wx/register-status?openid=${encodeURIComponent(openid)}`),
  followQrcode: () =>
    request<{ url: string; mp_name?: string }>('GET', '/wx/follow-qrcode'),
};

// 真实套题 API
export interface DeckItem {
  public_id: string;
  tenant_id: string;
  title: string;
  tag: string;
  description: string;
  status: string;
  created_at: string;
  total_cards: number;
  progress: number;
}

export interface QuestionItem {
  position: number;
  is_deep: boolean;
  public_id: string;
  type: string;       // single | multi | judge | deep
  stem: string;
  options: string[];
  answer: number | number[];
  explanation: string;
  difficulty: number;
  deck_id?: string;
  deck_title?: string;
  total_in_deck?: number;
  next_public_id?: string;
  // 过期卡降级/映射提示（后端 q_by_card 下发；expired=已过期但已定位到当前版本题目）
  degraded?: boolean;
  expired?: boolean;
  notice?: string;
}

export const deckApi = {
  list: () => authedRequest<{ items: DeckItem[]; total: number }>('GET', '/decks'),
  get: (id: string) => authedRequest<any>('GET', `/decks/${id}`),
  questions: (id: string) => authedRequest<{ items: QuestionItem[]; total: number }>('GET', `/decks/${id}/questions`),
};

export const learningApi = {
  startAttempt: (deckId: string) =>
    authedRequest<any>('POST', '/learning/attempts', { deck_id: deckId }),

  submitAnswer: (attemptId: string, questionId: string, userAnswer: number | number[], usedSeconds: number, idempotencyKey: string) =>
    authedRequest<any>('POST', `/learning/attempts/${attemptId}/answer`, {
      question_id: questionId, user_answer: userAnswer,
      used_seconds: usedSeconds, idempotency_key: idempotencyKey,
    }),

  submitSingleAnswer: (
    questionId: string,
    userAnswer: number[],
    deckId?: string,
    legacyGrade?: { isCorrect: boolean; score: number },
    // #75 本题作答耗时（秒）。此前该字段从未上报，导致证书「学习累计时间」恒为 0、
    // 综合排名里 10% 的时长权重完全没有区分度。
    usedSeconds?: number,
  ) =>
    authedRequest<any>('POST', '/learning/single-answer', {
      question_id: questionId,
      user_answer: userAnswer,
      deck_id: deckId || undefined,
      used_seconds: usedSeconds,
      // 滚动发布兼容：旧后端仍读这两个字段；新后端会忽略它们并自行判分。
      is_correct: legacyGrade?.isCorrect,
      score: legacyGrade?.score,
      idempotency_key: typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID()
        : `single-${questionId}-${Date.now()}-${Math.random().toString(16).slice(2)}`,
    }),

  recordView: (questionId: string) =>
    silentRequest<any>('POST', '/learning/record-view', { question_id: questionId }),

  getWrongBook: () => authedRequest<{ items: any[]; total: number }>('GET', '/learning/wrong-book'),
};

// 单题查询API（扫码答题用，需登录：关注服务号+员工号绑定）
export const questionApi = {
  getByCardNo: (cardNo: string | number) =>
    authedRequest<QuestionItem>('GET', `/q/${cardNo}`),
  getByPublicId: (publicId: string, deckId?: string) =>
    authedRequest<QuestionItem>('GET', `/question/${publicId}${deckId ? `?deck_id=${encodeURIComponent(deckId)}` : ''}`),
};

export const analyticsApi = {
  getStats: () => authedRequest<any>('GET', '/analytics/stats'),
  getLeaderboard: (limit = 50) => authedRequest<{ items: any[]; total: number }>('GET', `/analytics/leaderboard?limit=${limit}`),
};

// 人才学习档案（隐私：档案不显示姓名，凭人才数字ID验真）
export interface CertTenure {
  tenant_id: string;
  tenant_name: string;
  employee_code?: string;
  status: string;
  started_at: string | null;
  ended_at: string | null;
}
export interface CertMetrics {
  studyDays: number;
  studySets: number;
  studyDecks: number;
  totalAnswers: number;
  totalCorrect: number;
  accuracyRate: number;
  rank: number;
  rankTier: number;
  totalLearners: number;
}
export interface CertData {
  lifetime: boolean;
  platform_code: string | null;
  registered_at?: string | null;
  message?: string;
  metrics?: CertMetrics;
  weights?: { accuracy: number; days: number; sets: number; decks: number };
  verification_url?: string | null;
  tenures?: CertTenure[];
}
export const certApi = {
  // 我的人才学习档案（登录态）
  my: () => authedRequest<CertData>('GET', '/emp/my-cert'),
  // 公开验真（凭人才数字ID，无需登录）
  verify: (code: string) => request<CertData>('GET', `/cert/${encodeURIComponent(code)}`),
};

// 个人中心（GET /me）
export interface MeInfo {
  code: string;
  public_id: string;
  tenant_id: string;
  tenant_name?: string;
  status: string;
}

export const meApi = {
  get: () => authedRequest<{ employee: MeInfo }>('GET', '/me'),
};

// 套题结算（服务端取数，兼容未来的 attempt 会话链路）
export const resultApi = {
  getAttempt: (attemptId: string) => authedRequest<any>('GET', `/learning/attempts/${attemptId}/result`),
};

// 知识库 API
export interface KmModule {
  id: number;
  public_id: string;
  title: string;
  category: string;
  source: string;
  created_at: string;
  point_count: number;
}

export interface KmPoint {
  id: number;
  public_id: string;
  title: string;
  ordinal?: number;
  content?: string;
  image_url: string;
  image_file?: string;
  module_id: number;
  module_title?: string;
  category?: string;
  keywords?: string[];
  score?: number;
}

export interface KmSearchOptions {
  topK?: number;
  tag?: string | string[];
  category?: string;
  semantic?: boolean;
}

export const kmApi = {
  listModules: () => authedRequest<{ items: KmModule[]; total: number }>('GET', '/km/modules'),
  listPoints: (limit = 50, offset = 0) =>
    authedRequest<{ items: KmPoint[]; total: number }>('GET', `/km/points?limit=${limit}&offset=${offset}`),
  getPoint: (id: number) => authedRequest<KmPoint>('GET', `/km/points/${id}`),
  listPointsByModule: (moduleId: number) =>
    authedRequest<{ items: KmPoint[]; total: number }>('GET', `/km/modules/${moduleId}/points`),
  search: (query: string, opts: KmSearchOptions = {}) => {
    const { topK = 8, tag, category, semantic = true } = opts;
    const payload: Record<string, unknown> = { query, top_k: topK, semantic };
    if (tag) payload.tag = tag;
    if (category) payload.category = category;
    return authedRequest<{ items: KmPoint[]; total: number; query: string; tags?: string[]; semantic?: boolean }>(
      'POST',
      '/km/search',
      payload,
    );
  },
};

// ============ 满意度调查（需求书模块七） ============
export interface SurveyQuestionDef {
  key: string;
  type: 'score' | 'single' | 'text';
  title: string;
  required?: boolean;
  options?: string[];
}
export interface SurveyDetail {
  id: string;
  title: string;
  intro: string;
  status: 'draft' | 'published' | 'closed';
  questions: SurveyQuestionDef[];
  answered?: boolean;
  myAnswers?: Record<string, number | string>;
}

export const surveyApi = {
  pending: () =>
    authedRequest<{ pending: { id: string; title: string; intro: string } | null }>('GET', '/emp/surveys/pending'),
  get: (id: string) => authedRequest<SurveyDetail>('GET', `/emp/surveys/${id}`),
  submit: (id: string, answers: Record<string, number | string>) =>
    authedRequest<{ id: string }>('POST', `/emp/surveys/${id}/submit`, { answers }),
};
